// THE SCOPE BAR'S RULES — which tiers a lens may change, which pods it can see,
// and which pod and brand are lit. Pure, so the Videos page, the Schedule page
// and the tests all read one answer.
//
// Three tiers, pod → brand → creator, and each one is in one of three modes:
//
//   pick  pills, and pressing one pushes an address
//   text  the one value there is, said in words — it tells you where you are
//   none  not drawn: it would only name yourself, or it is internal
//
// ⚠️ A POD NAME NEVER REACHES A CREATOR OR A BRAND. Pods are internal. The
// layout hands pod data only to an identity in the AM's seat, and this file
// hands a lens outside that seat no pods at all — so an admin looking through a
// creator's lens sees what the creator would, and nothing a creator could not.

import type { Persona, PodLead } from "@/lib/persona-types";
import { sitsInAmSeat } from "@/lib/persona-scope";

export type TierMode = "pick" | "text" | "none";

export interface ScopeTiers {
  pod: TierMode;
  brand: TierMode;
  creator: TierMode;
}

/**
 * The "No pod" pill's value, and what `?pod=` says for it. `none` is a reserved
 * person slug (lib/slug.ts), so no lead's pod can ever be spelled the same.
 */
export const NO_POD = "none";

/** A pod as the lens sees it: its pill, and the lens's own brands inside it. */
export interface ScopePod extends PodLead {
  /** Brand slugs, narrowed to the lens. Never empty — a pod with none is not listed. */
  brands: string[];
}

/**
 * THE PODS THIS LENS CAN SEE — the identity's pods (from the layout), narrowed to
 * the lens's brands, plus "No pod" for the lens's brands nobody leads.
 *
 * Empty for a lens outside the AM's seat, since pods are internal, which is why
 * a creator lens on an admin's browser renders no pod name even though the
 * browser holds some. A pod none of whose brands the lens can see is left out:
 * pressing it would open an empty brand tier.
 *
 * Unordered; `podAndBrand` sorts, because the label it sorts on and the
 * "No pod goes last" rule belong with the default it picks.
 */
export function podsInView(
  lens: Persona,
  visibleBrands: readonly string[],
  brandPods: Readonly<Record<string, string>>,
  podLeads: readonly PodLead[],
): ScopePod[] {
  if (!sitsInAmSeat(lens.role)) return [];
  const byValue = new Map<string, ScopePod>();
  const unowned: string[] = [];
  const lead = new Map(podLeads.map((p) => [p.value, p]));
  for (const slug of visibleBrands) {
    const value = Object.hasOwn(brandPods, slug) ? brandPods[slug] : undefined;
    const pod = value !== undefined ? lead.get(value) : undefined;
    if (!pod) {
      unowned.push(slug);
      continue;
    }
    const seen = byValue.get(pod.value);
    if (seen) seen.brands.push(slug);
    else byValue.set(pod.value, { ...pod, brands: [slug] });
  }
  const out = [...byValue.values()];
  if (unowned.length) out.push({ value: NO_POD, slug: NO_POD, label: "No pod", brands: unowned });
  return out;
}

/**
 * WHICH TIERS THIS LENS MAY CHANGE, role by role.
 *
 *   admin     pod pick · brand pick · creator pick
 *   am_ops,
 *   creative_specialist
 *             pod pick (text when their brands sit in one pod) · brand pick · creator pick
 *   am        pod text ("Maya's pod") · brand pick · creator pick
 *   creator,
 *   writer    no pod · brand pick with 2+ brands, text with one · no creator
 *   brand     no pod · brand pick with 2+ brands, text with one · creator pick
 *
 * `pods` and `brands` are how many the lens can see. A tier with nothing in it
 * is `none` whatever the role — there is nothing to say — and an AM's brand and
 * creator tiers stay `pick` with one pill, as the Videos rows always have: a
 * lone lit pill says which brand the page is about.
 */
export function scopeTiersFor(role: Persona["role"], counts: { pods: number; brands: number }): ScopeTiers {
  const { pods, brands } = counts;
  const brandTier = (mode: TierMode): TierMode => (brands === 0 ? "none" : mode);
  switch (role) {
    case "admin":
      return { pod: pods ? "pick" : "none", brand: brandTier("pick"), creator: "pick" };
    case "am_ops":
    // A CREATIVE SPECIALIST'S BRANDS ARE PICKED ONE BY ONE, so they can sit in
    // several pods, which is AM Ops' shape and not the AM's.
    case "creative_specialist":
      return { pod: pods > 1 ? "pick" : pods === 1 ? "text" : "none", brand: brandTier("pick"), creator: "pick" };
    case "am":
      return { pod: pods ? "text" : "none", brand: brandTier("pick"), creator: "pick" };
    case "creator":
    case "writer":
      return { pod: "none", brand: brandTier(brands > 1 ? "pick" : "text"), creator: "none" };
    case "brand":
      // A BRAND USER MAY MANAGE SEVERAL BRANDS, and gets the creator's rule for
      // them: pills with two, words with one. An admin's brand lens is always
      // one brand, so it reads as before.
      return { pod: "none", brand: brandTier(brands > 1 ? "pick" : "text"), creator: "pick" };
    case "creator_expert":
      // No Scripts page at all (lib/nav-config.ts). Said, so the switch is whole.
      return { pod: "none", brand: "none", creator: "none" };
  }
}

/** A pod pill: its value, its label, and how many of the page's brands it holds, as /brands' pod pills do. */
export interface PodPill {
  value: string;
  label: string;
  count: number;
}

/**
 * WHICH POD AND WHICH BRAND ARE LIT — the pod half of the scope bar, shared by
 * the Videos page (through `deriveVideoPicker`) and the Schedule page, which has
 * no creator tier and so no scripts to derive one from.
 *
 *   1. the brand decides the pod, when the picked brand is on the page at all;
 *   2. otherwise the pod is the picked one if it is listed, else the first;
 *   3. the brand row is that pod's brands;
 *   4. the brand is the picked one if it is in that row, else the row's first.
 *
 * `brands` is every brand the page can show, already in the order the row reads
 * (alphabetical by label). With `pods` empty — every lens outside the AM's seat —
 * step 3 is the whole list and there is no pod, exactly as before the tier
 * existed.
 *
 * EVERY BRAND ON THE PAGE IS IN EXACTLY ONE POD. A brand no pod lists — one
 * only the scripts know about, a deleted brand's leftovers — joins "No pod",
 * which is made for it if the lens had none: a brand the tier cannot reach
 * would be a brand the page cannot show at all.
 *
 * A pick naming nothing on the page falls back to the first pill, never an
 * error — the same rule that absorbs a stale pick left behind by a lens switch.
 */
export function podAndBrand<B extends { value: string; label: string }>(opts: {
  brands: readonly B[];
  pods: readonly { value: string; label: string; brands: readonly string[] }[];
  pickedPod: string | null;
  pickedBrand: string | null;
}): { pods: PodPill[]; pod: string | undefined; brands: B[]; brand: string | undefined } {
  const { brands: named, pods: podsIn, pickedPod, pickedBrand } = opts;
  const has = (list: readonly { value: string }[], v: string | null) => Boolean(v && list.some((o) => o.value === v));

  let pods: PodPill[] = [];
  let pod: string | undefined;
  let brands = [...named];
  if (podsIn.length) {
    const podOf = new Map<string, string>();
    for (const p of podsIn) for (const b of p.brands) podOf.set(b, p.value);
    const members = new Map<string, string[]>(podsIn.map((p) => [p.value, []]));
    const labels = new Map(podsIn.map((p) => [p.value, p.label]));
    for (const b of named) {
      const v = podOf.get(b.value) ?? NO_POD;
      if (!members.has(v)) {
        members.set(v, []);
        labels.set(v, "No pod");
      }
      members.get(v)!.push(b.value);
    }
    pods = [...members.entries()]
      .filter(([, bs]) => bs.length > 0)
      .map(([value, bs]) => ({ value, label: labels.get(value)!, count: bs.length }))
      // Alphabetical, the rule every row keeps — and "No pod" LAST, because it
      // is the leftovers and not a pod anybody leads.
      .sort((a, b) => Number(a.value === NO_POD) - Number(b.value === NO_POD) || a.label.localeCompare(b.label));

    // THE BRAND DECIDES THE POD: a link naming a brand opens that brand's
    // pod whatever `?pod=` says, so an old `?brand=` link needs no pod in it.
    pod = has(named, pickedBrand)
      ? (podOf.get(pickedBrand!) ?? NO_POD)
      : has(pods, pickedPod) ? pickedPod! : pods[0]?.value;
    const inPod = new Set(members.get(pod ?? "") ?? []);
    brands = named.filter((b) => inPod.has(b.value));
  }

  const brand = has(brands, pickedBrand) ? pickedBrand! : brands[0]?.value;
  return { pods, pod, brands, brand };
}
